// SPDX-FileCopyrightText: 2017-2026 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

// GENERATED FILE: no manual modifications

import type { Action } from '../action'
import type { ApplicationId } from './shared'
import type { ApplicationType } from './application'
import type { CitizenAuthLevel } from './shared'
import type { CitizenFeatures } from './shared'
import DateRange from '../../date-range'
import type { DaycareId } from './shared'
import type { EmployeeFeatureConfig } from './shared'
import type { EmployeeFeatures } from './shared'
import type { EmployeeId } from './shared'
import type { EvakaUser } from './user'
import type { EvakaUserId } from './shared'
import type { FosterParentId } from './shared'
import type { GroupId } from './shared'
import HelsinkiDateTime from '../../helsinki-date-time'
import type { IncomeEffect } from './invoicing'
import type { JsonOf } from '../../json'
import LocalDate from '../../local-date'
import type { MobileDevice } from './pairing'
import type { Nationality } from './vtjclient'
import type { NativeLanguage } from './vtjclient'
import type { ParentshipId } from './shared'
import type { PartnershipId } from './shared'
import type { PersonEmailVerificationId } from './shared'
import type { PersonId } from './shared'
import type { ProviderType } from './daycare'
import type { UserRole } from './shared'

/**
* Generated from evaka.core.pis.controllers.PersonController.AddSsnRequest
*/
export interface AddSsnRequest {
  ssn: string
}

/**
* Generated from evaka.core.pis.CitizenUserDetails
*/
export interface CitizenUserDetails {
  backupPhone: string
  email: string | null
  firstName: string
  id: PersonId
  lastName: string
  phone: string
  postOffice: string
  postalCode: string
  preferredName: string
  streetAddress: string
  weakLoginUsername: string | null
}

/**
* Generated from evaka.core.pis.SystemController.CitizenUserResponse
*/
export interface CitizenUserResponse {
  accessibleFeatures: CitizenFeatures
  authLevel: CitizenAuthLevel
  details: CitizenUserDetails
}

/**
* Generated from evaka.core.pis.controllers.CreateFosterParentRelationshipBody
*/
export interface CreateFosterParentRelationshipBody {
  childId: PersonId
  parentId: PersonId
  validDuring: DateRange
}

/**
* Generated from evaka.core.pis.controllers.CreatePersonBody
*/
export interface CreatePersonBody {
  dateOfBirth: LocalDate
  email: string | null
  firstName: string
  lastName: string
  phone: string
  postOffice: string
  postalCode: string
  streetAddress: string
}

/**
* Generated from evaka.core.pis.CreateSource
*/
export type CreateSource =
  | 'USER'
  | 'APPLICATION'
  | 'DVV'

/**
* Generated from evaka.core.pis.controllers.EmployeeController.CreateSsnEmployeeResponse
*/
export interface CreateSsnEmployeeResponse {
  id: EmployeeId
}

/**
* Generated from evaka.core.pis.CreationModificationMetadata
*/
export interface CreationModificationMetadata {
  createSource: CreateSource | null
  createdAt: HelsinkiDateTime | null
  createdBy: EvakaUserId | null
  createdByName: string | null
  createdFromApplication: ApplicationId | null
  createdFromApplicationCreated: HelsinkiDateTime | null
  createdFromApplicationType: ApplicationType | null
  modifiedAt: HelsinkiDateTime | null
  modifiedBy: EvakaUserId | null
  modifiedByName: string | null
  modifySource: ModifySource | null
}

/**
* Generated from evaka.core.pis.DaycareGroupRole
*/
export interface DaycareGroupRole {
  daycareId: DaycareId
  daycareName: string
  groupId: GroupId
  groupName: string
}

/**
* Generated from evaka.core.pis.DaycareRole
*/
export interface DaycareRole {
  daycareId: DaycareId
  daycareName: string
  endDate: LocalDate | null
  role: UserRole
}

/**
* Generated from evaka.core.pis.controllers.PersonController.DisableSsnRequest
*/
export interface DisableSsnRequest {
  disabled: boolean
}

/**
* Generated from evaka.core.pis.EmailMessageType
*/
export const emailMessageTypes = [
  'TRANSACTIONAL',
  'MESSAGE_NOTIFICATION',
  'BULLETIN_NOTIFICATION',
  'INCOME_NOTIFICATION',
  'CALENDAR_EVENT_NOTIFICATION',
  'DECISION_NOTIFICATION',
  'DOCUMENT_NOTIFICATION',
  'INFORMAL_DOCUMENT_NOTIFICATION',
  'ATTENDANCE_RESERVATION_NOTIFICATION',
  'DISCUSSION_TIME_NOTIFICATION'
] as const

export type EmailMessageType = typeof emailMessageTypes[number]

/**
* Generated from evaka.core.pis.EmailVerification
*/
export interface EmailVerification {
  email: string
  expiresAt: HelsinkiDateTime
  id: PersonEmailVerificationId
  sentAt: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.pis.controllers.PersonalDataControllerCitizen.EmailVerificationRequest
*/
export interface EmailVerificationRequest {
  code: string
  id: PersonEmailVerificationId
}

/**
* Generated from evaka.core.pis.controllers.PersonalDataControllerCitizen.EmailVerificationStatusResponse
*/
export interface EmailVerificationStatusResponse {
  email: string | null
  latestVerification: EmailVerification | null
  verifiedEmail: string | null
}

/**
* Generated from evaka.core.pis.Employee
*/
export interface Employee {
  active: boolean
  created: HelsinkiDateTime
  email: string | null
  externalId: string | null
  firstName: string
  hasSsn: boolean
  id: EmployeeId
  lastLogin: HelsinkiDateTime | null
  lastName: string
  preferredFirstName: string | null
  temporaryInUnitId: DaycareId | null
  updated: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.pis.SystemController.EmployeeAuthResponse
*/
export interface EmployeeAuthResponse {
  featureConfig: EmployeeFeatureConfig
  user: EmployeeUserResponse
}

/**
* Generated from evaka.core.pis.controllers.EmployeeController.EmployeeEmailRequest
*/
export interface EmployeeEmailRequest {
  email: string | null
}

/**
* Generated from evaka.core.pis.controllers.EmployeeController.EmployeePreferredFirstName
*/
export interface EmployeePreferredFirstName {
  preferredFirstName: string | null
  preferredFirstNameOptions: string[]
}

/**
* Generated from evaka.core.pis.controllers.EmployeeController.EmployeeSetPreferredFirstNameUpdateRequest
*/
export interface EmployeeSetPreferredFirstNameUpdateRequest {
  preferredFirstName: string | null
}

/**
* Generated from evaka.core.pis.EmployeeStartPage
*/
export type EmployeeStartPage =
  | 'APPLICATIONS'
  | 'UNITS'
  | 'REPORTS'
  | 'MESSAGES'
  | 'WELCOME'
  | 'SEARCH'

/**
* Generated from evaka.core.pis.SystemController.EmployeeUserResponse
*/
export interface EmployeeUserResponse {
  accessibleFeatures: EmployeeFeatures
  allScopedRoles: UserRole[]
  firstName: string
  globalRoles: UserRole[]
  id: EmployeeId
  lastName: string
  permittedGlobalActions: Action.Global[]
  startPage: EmployeeStartPage
}

/**
* Generated from evaka.core.pis.EmployeeWithDaycareRoles
*/
export interface EmployeeWithDaycareRoles {
  active: boolean
  canSetAsAdmin: boolean
  created: HelsinkiDateTime
  daycareGroupRoles: DaycareGroupRole[]
  daycareRoles: DaycareRole[]
  email: string | null
  employeeNumber: string | null
  externalId: string | null
  firstName: string
  globalRoles: UserRole[]
  hasSsn: boolean
  id: EmployeeId
  lastLogin: HelsinkiDateTime | null
  lastName: string
  personalMobileDevices: MobileDevice[]
  scheduledDaycareRoles: ScheduledDaycareRole[]
  temporaryUnitName: string | null
  updated: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.pis.controllers.PersonController.EvakaRightsRequest
*/
export interface EvakaRightsRequest {
  denied: boolean
  guardianId: PersonId
}

/**
* Generated from evaka.core.pis.FamilyContact
*/
export interface FamilyContact {
  backupPhone: string
  email: string | null
  firstName: string
  id: PersonId
  lastName: string
  phone: string
  postOffice: string
  postalCode: string
  priority: number | null
  role: FamilyContactRole
  streetAddress: string
}

/**
* Generated from evaka.core.pis.controllers.FamilyContactPriorityUpdate
*/
export interface FamilyContactPriorityUpdate {
  childId: PersonId
  contactPersonId: PersonId
  priority: number | null
}

/**
* Generated from evaka.core.pis.FamilyContactRole
*/
export type FamilyContactRole =
  | 'LOCAL_GUARDIAN'
  | 'LOCAL_FOSTER_PARENT'
  | 'LOCAL_ADULT'
  | 'LOCAL_SIBLING'
  | 'REMOTE_GUARDIAN'
  | 'REMOTE_FOSTER_PARENT'

/**
* Generated from evaka.core.pis.controllers.FamilyContactUpdate
*/
export interface FamilyContactUpdate {
  backupPhone: string | null
  childId: PersonId
  contactPersonId: PersonId
  email: string | null
  phone: string | null
}

/**
* Generated from evaka.core.pis.service.FamilyMember
*/
export interface FamilyMember {
  firstName: string
  lastName: string
  personId: PersonId
}

/**
* Generated from evaka.core.pis.service.FamilyMembers
*/
export interface FamilyMembers {
  adults: FamilyMember[]
  children: FamilyMember[]
}

/**
* Generated from evaka.core.pis.service.FamilyOverview
*/
export interface FamilyOverview {
  children: FamilyOverviewPerson[]
  headOfFamily: FamilyOverviewPerson
  partner: FamilyOverviewPerson | null
  totalIncome: FamilyOverviewIncome | null
}

/**
* Generated from evaka.core.pis.service.FamilyOverviewIncome
*/
export interface FamilyOverviewIncome {
  effect: IncomeEffect | null
  total: number | null
}

/**
* Generated from evaka.core.pis.service.FamilyOverviewPerson
*/
export interface FamilyOverviewPerson {
  dateOfBirth: LocalDate
  firstName: string
  headOfChild: PersonId | null
  income: FamilyOverviewIncome | null
  lastName: string
  personId: PersonId
  postOffice: string
  postalCode: string
  restrictedDetailsEnabled: boolean
  streetAddress: string
}

/**
* Generated from evaka.core.pis.controllers.FosterParentRelationship
*/
export interface FosterParentRelationship {
  child: PersonSummary
  modifiedAt: HelsinkiDateTime
  modifiedBy: EvakaUser
  parent: PersonSummary
  relationshipId: FosterParentId
  validDuring: DateRange
}

/**
* Generated from evaka.core.pis.controllers.GetOrCreatePersonBySsnRequest
*/
export interface GetOrCreatePersonBySsnRequest {
  readonly: boolean
  ssn: string
}

/**
* Generated from evaka.core.pis.controllers.PersonController.GuardiansResponse
*/
export interface GuardiansResponse {
  blockedGuardians: PersonJSON[] | null
  guardians: PersonJSON[]
}

/**
* Generated from evaka.core.pis.controllers.PersonController.MergeRequest
*/
export interface MergeRequest {
  duplicate: PersonId
  master: PersonId
}

/**
* Generated from evaka.core.pis.ModifySource
*/
export type ModifySource =
  | 'USER'
  | 'DVV'

/**
* Generated from evaka.core.pis.NewSsnEmployee
*/
export interface NewSsnEmployee {
  email: string | null
  firstName: string
  lastName: string
  ssn: string
}

/**
* Generated from evaka.core.pis.service.PersonAddressDTO.Origin
*/
export type Origin =
  | 'VTJ'
  | 'MUNICIPAL'
  | 'EVAKA'

/**
* Generated from evaka.core.pis.service.ParentshipDetailed
*/
export interface ParentshipDetailed {
  child: PersonJSON
  childId: PersonId
  conflict: boolean
  creationModificationMetadata: CreationModificationMetadata
  endDate: LocalDate
  headOfChild: PersonJSON
  headOfChildId: PersonId
  id: ParentshipId
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.ParentshipController.ParentshipRequest
*/
export interface ParentshipRequest {
  childId: PersonId
  endDate: LocalDate
  headOfChildId: PersonId
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.ParentshipController.ParentshipUpdateRequest
*/
export interface ParentshipUpdateRequest {
  endDate: LocalDate
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.ParentshipController.ParentshipWithPermittedActions
*/
export interface ParentshipWithPermittedActions {
  data: ParentshipDetailed
  permittedActions: Action.Parentship[]
}

/**
* Generated from evaka.core.pis.service.Partnership
*/
export interface Partnership {
  conflict: boolean
  creationModificationMetadata: CreationModificationMetadata
  endDate: LocalDate | null
  id: PartnershipId
  partners: PersonJSON[]
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.PartnershipsController.PartnershipRequest
*/
export interface PartnershipRequest {
  endDate: LocalDate | null
  person1Id: PersonId
  person2Id: PersonId
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.PartnershipsController.PartnershipUpdateRequest
*/
export interface PartnershipUpdateRequest {
  endDate: LocalDate | null
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.PartnershipsController.PartnershipWithPermittedActions
*/
export interface PartnershipWithPermittedActions {
  data: Partnership
  permittedActions: Action.Partnership[]
}

/**
* Generated from evaka.core.pis.service.PersonAddressDTO
*/
export interface PersonAddressDTO {
  origin: Origin
  postOffice: string
  postalCode: string
  residenceCode: string
  streetAddress: string
}

/**
* Generated from evaka.core.pis.service.PersonBasicInfo
*/
export interface PersonBasicInfo {
  dateOfBirth: LocalDate
  dateOfDeath: LocalDate | null
  duplicateOf: PersonId | null
  firstName: string
  hasSsn: boolean
  id: PersonId
  lastName: string
  restrictedDetailsEnabled: boolean
  ssnAddingDisabled: boolean
}

/**
* Generated from evaka.core.pis.service.PersonJSON
*/
export interface PersonJSON {
  backupPhone: string
  dateOfBirth: LocalDate
  dateOfDeath: LocalDate | null
  duplicateOf: PersonId | null
  email: string | null
  firstName: string
  forceManualFeeDecisions: boolean
  id: PersonId
  invoiceRecipientName: string
  invoicingPostOffice: string
  invoicingPostalCode: string
  invoicingStreetAddress: string
  language: string | null
  lastName: string
  municipalityOfResidence: string
  ophPersonOid: string | null
  phone: string
  postOffice: string
  postalCode: string
  residenceCode: string
  restrictedDetailsEnabled: boolean
  socialSecurityNumber: string | null
  ssnAddingDisabled: boolean
  streetAddress: string
  updatedFromVtj: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.pis.PersonNameDetails
*/
export interface PersonNameDetails {
  firstName: string
  id: PersonId
  lastName: string
}

/**
* Generated from evaka.core.pis.service.PersonPatch
*/
export interface PersonPatch {
  backupPhone: string | null
  dateOfBirth: LocalDate | null
  email: string | null
  firstName: string | null
  forceManualFeeDecisions: boolean | null
  invoiceRecipientName: string | null
  invoicingPostOffice: string | null
  invoicingPostalCode: string | null
  invoicingStreetAddress: string | null
  lastName: string | null
  municipalityOfResidence: string | null
  ophPersonOid: string | null
  phone: string | null
  postOffice: string | null
  postalCode: string | null
  streetAddress: string | null
}

/**
* Generated from evaka.core.pis.controllers.PersonController.PersonResponse
*/
export interface PersonResponse {
  permittedActions: Action.Person[]
  person: PersonBasicInfo
}

/**
* Generated from evaka.core.pis.service.PersonSensitiveDetails
*/
export interface PersonSensitiveDetails {
  backupPhone: string
  email: string | null
  forceManualFeeDecisions: boolean
  invoiceRecipientName: string
  invoicingPostOffice: string
  invoicingPostalCode: string
  invoicingStreetAddress: string
  language: string | null
  municipalityOfResidence: string
  ophPersonOid: string | null
  phone: string
  postOffice: string
  postalCode: string
  residenceCode: string
  socialSecurityNumber: string | null
  streetAddress: string
  updatedFromVtj: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.pis.PersonSummary
*/
export interface PersonSummary {
  dateOfBirth: LocalDate
  dateOfDeath: LocalDate | null
  firstName: string
  id: PersonId
  lastName: string
  restrictedDetailsEnabled: boolean
  socialSecurityNumber: string | null
  streetAddress: string
}

/**
* Generated from evaka.core.pis.service.PersonWithChildrenDTO
*/
export interface PersonWithChildrenDTO {
  address: PersonAddressDTO
  backupPhone: string
  children: PersonWithChildrenDTO[]
  dateOfBirth: LocalDate
  dateOfDeath: LocalDate | null
  duplicateOf: PersonId | null
  email: string | null
  firstName: string
  id: PersonId
  lastName: string
  municipalityOfResidence: string
  nationalities: Nationality[]
  nativeLanguage: NativeLanguage | null
  phone: string
  preferredName: string
  residenceCode: string
  restrictedDetails: RestrictedDetails
  socialSecurityNumber: string | null
}

/**
* Generated from evaka.core.pis.PersonalDataUpdate
*/
export interface PersonalDataUpdate {
  backupPhone: string
  email: string
  phone: string
  preferredName: string
}

/**
* Generated from evaka.core.pis.controllers.PinCode
*/
export interface PinCode {
  pin: string
}

/**
* Generated from evaka.core.pis.SystemController.PinLoginEmployee
*/
export interface PinLoginEmployee {
  firstName: string
  lastName: string
}

/**
* Generated from evaka.core.pis.SystemController.PinLoginResponse
*/
export interface PinLoginResponse {
  employee: PinLoginEmployee | null
  status: PinLoginStatus
}

/**
* Generated from evaka.core.pis.SystemController.PinLoginStatus
*/
export type PinLoginStatus =
  | 'SUCCESS'
  | 'WRONG_PIN'
  | 'PIN_LOCKED'

/**
* Generated from evaka.core.pis.service.RestrictedDetails
*/
export interface RestrictedDetails {
  enabled: boolean
  endDate: LocalDate | null
}

/**
* Generated from evaka.core.pis.ScheduledDaycareRole
*/
export interface ScheduledDaycareRole {
  daycareId: DaycareId
  daycareName: string
  endDate: LocalDate | null
  role: UserRole
  startDate: LocalDate
}

/**
* Generated from evaka.core.pis.controllers.SearchEmployeeRequest
*/
export interface SearchEmployeeRequest {
  globalRoles: UserRole[] | null
  hideDeactivated: boolean | null
  searchTerm: string | null
  unitProviderTypes: ProviderType[] | null
  unitRoles: UserRole[] | null
}

/**
* Generated from evaka.core.pis.controllers.SearchPersonBody
*/
export interface SearchPersonBody {
  orderBy: string
  searchTerm: string
  sortDirection: string
}

/**
* Generated from evaka.core.pis.TemporaryEmployee
*/
export interface TemporaryEmployee {
  firstName: string
  groupIds: GroupId[]
  hasStaffOccupancyEffect: boolean
  lastName: string
  pinCode: PinCode | null
}

/**
* Generated from evaka.core.pis.controllers.PersonalDataControllerCitizen.UpdateWeakLoginCredentialsRequest
*/
export interface UpdateWeakLoginCredentialsRequest {
  password: string
}

/**
* Generated from evaka.core.pis.controllers.EmployeeController.UpsertEmployeeDaycareRolesRequest
*/
export interface UpsertEmployeeDaycareRolesRequest {
  daycareIds: DaycareId[]
  endDate: LocalDate | null
  role: UserRole
  startDate: LocalDate
}


export function deserializeJsonCreateFosterParentRelationshipBody(json: JsonOf<CreateFosterParentRelationshipBody>): CreateFosterParentRelationshipBody {
  return {
    ...json,
    validDuring: DateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonCreatePersonBody(json: JsonOf<CreatePersonBody>): CreatePersonBody {
  return {
    ...json,
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth)
  }
}


export function deserializeJsonCreationModificationMetadata(json: JsonOf<CreationModificationMetadata>): CreationModificationMetadata {
  return {
    ...json,
    createdAt: (json.createdAt != null) ? HelsinkiDateTime.parseIso(json.createdAt) : null,
    createdFromApplicationCreated: (json.createdFromApplicationCreated != null) ? HelsinkiDateTime.parseIso(json.createdFromApplicationCreated) : null,
    modifiedAt: (json.modifiedAt != null) ? HelsinkiDateTime.parseIso(json.modifiedAt) : null
  }
}


export function deserializeJsonDaycareRole(json: JsonOf<DaycareRole>): DaycareRole {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null
  }
}


export function deserializeJsonEmailVerification(json: JsonOf<EmailVerification>): EmailVerification {
  return {
    ...json,
    expiresAt: HelsinkiDateTime.parseIso(json.expiresAt),
    sentAt: (json.sentAt != null) ? HelsinkiDateTime.parseIso(json.sentAt) : null
  }
}


export function deserializeJsonEmailVerificationStatusResponse(json: JsonOf<EmailVerificationStatusResponse>): EmailVerificationStatusResponse {
  return {
    ...json,
    latestVerification: (json.latestVerification != null) ? deserializeJsonEmailVerification(json.latestVerification) : null
  }
}


export function deserializeJsonEmployee(json: JsonOf<Employee>): Employee {
  return {
    ...json,
    created: HelsinkiDateTime.parseIso(json.created),
    lastLogin: (json.lastLogin != null) ? HelsinkiDateTime.parseIso(json.lastLogin) : null,
    updated: (json.updated != null) ? HelsinkiDateTime.parseIso(json.updated) : null
  }
}


export function deserializeJsonEmployeeWithDaycareRoles(json: JsonOf<EmployeeWithDaycareRoles>): EmployeeWithDaycareRoles {
  return {
    ...json,
    created: HelsinkiDateTime.parseIso(json.created),
    daycareRoles: json.daycareRoles.map(e => deserializeJsonDaycareRole(e)),
    lastLogin: (json.lastLogin != null) ? HelsinkiDateTime.parseIso(json.lastLogin) : null,
    scheduledDaycareRoles: json.scheduledDaycareRoles.map(e => deserializeJsonScheduledDaycareRole(e)),
    updated: (json.updated != null) ? HelsinkiDateTime.parseIso(json.updated) : null
  }
}


export function deserializeJsonFamilyOverview(json: JsonOf<FamilyOverview>): FamilyOverview {
  return {
    ...json,
    children: json.children.map(e => deserializeJsonFamilyOverviewPerson(e)),
    headOfFamily: deserializeJsonFamilyOverviewPerson(json.headOfFamily),
    partner: (json.partner != null) ? deserializeJsonFamilyOverviewPerson(json.partner) : null
  }
}


export function deserializeJsonFamilyOverviewPerson(json: JsonOf<FamilyOverviewPerson>): FamilyOverviewPerson {
  return {
    ...json,
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth)
  }
}


export function deserializeJsonFosterParentRelationship(json: JsonOf<FosterParentRelationship>): FosterParentRelationship {
  return {
    ...json,
    child: deserializeJsonPersonSummary(json.child),
    modifiedAt: HelsinkiDateTime.parseIso(json.modifiedAt),
    parent: deserializeJsonPersonSummary(json.parent),
    validDuring: DateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonGuardiansResponse(json: JsonOf<GuardiansResponse>): GuardiansResponse {
  return {
    ...json,
    blockedGuardians: (json.blockedGuardians != null) ? json.blockedGuardians.map(e => deserializeJsonPersonJSON(e)) : null,
    guardians: json.guardians.map(e => deserializeJsonPersonJSON(e))
  }
}


export function deserializeJsonParentshipDetailed(json: JsonOf<ParentshipDetailed>): ParentshipDetailed {
  return {
    ...json,
    child: deserializeJsonPersonJSON(json.child),
    creationModificationMetadata: deserializeJsonCreationModificationMetadata(json.creationModificationMetadata),
    endDate: LocalDate.parseIso(json.endDate),
    headOfChild: deserializeJsonPersonJSON(json.headOfChild),
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonParentshipRequest(json: JsonOf<ParentshipRequest>): ParentshipRequest {
  return {
    ...json,
    endDate: LocalDate.parseIso(json.endDate),
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonParentshipUpdateRequest(json: JsonOf<ParentshipUpdateRequest>): ParentshipUpdateRequest {
  return {
    ...json,
    endDate: LocalDate.parseIso(json.endDate),
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonParentshipWithPermittedActions(json: JsonOf<ParentshipWithPermittedActions>): ParentshipWithPermittedActions {
  return {
    ...json,
    data: deserializeJsonParentshipDetailed(json.data)
  }
}


export function deserializeJsonPartnership(json: JsonOf<Partnership>): Partnership {
  return {
    ...json,
    creationModificationMetadata: deserializeJsonCreationModificationMetadata(json.creationModificationMetadata),
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null,
    partners: json.partners.map(e => deserializeJsonPersonJSON(e)),
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonPartnershipRequest(json: JsonOf<PartnershipRequest>): PartnershipRequest {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null,
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonPartnershipUpdateRequest(json: JsonOf<PartnershipUpdateRequest>): PartnershipUpdateRequest {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null,
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonPartnershipWithPermittedActions(json: JsonOf<PartnershipWithPermittedActions>): PartnershipWithPermittedActions {
  return {
    ...json,
    data: deserializeJsonPartnership(json.data)
  }
}


export function deserializeJsonPersonBasicInfo(json: JsonOf<PersonBasicInfo>): PersonBasicInfo {
  return {
    ...json,
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth),
    dateOfDeath: (json.dateOfDeath != null) ? LocalDate.parseIso(json.dateOfDeath) : null
  }
}


export function deserializeJsonPersonJSON(json: JsonOf<PersonJSON>): PersonJSON {
  return {
    ...json,
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth),
    dateOfDeath: (json.dateOfDeath != null) ? LocalDate.parseIso(json.dateOfDeath) : null,
    updatedFromVtj: (json.updatedFromVtj != null) ? HelsinkiDateTime.parseIso(json.updatedFromVtj) : null
  }
}


export function deserializeJsonPersonPatch(json: JsonOf<PersonPatch>): PersonPatch {
  return {
    ...json,
    dateOfBirth: (json.dateOfBirth != null) ? LocalDate.parseIso(json.dateOfBirth) : null
  }
}


export function deserializeJsonPersonResponse(json: JsonOf<PersonResponse>): PersonResponse {
  return {
    ...json,
    person: deserializeJsonPersonBasicInfo(json.person)
  }
}


export function deserializeJsonPersonSensitiveDetails(json: JsonOf<PersonSensitiveDetails>): PersonSensitiveDetails {
  return {
    ...json,
    updatedFromVtj: (json.updatedFromVtj != null) ? HelsinkiDateTime.parseIso(json.updatedFromVtj) : null
  }
}


export function deserializeJsonPersonSummary(json: JsonOf<PersonSummary>): PersonSummary {
  return {
    ...json,
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth),
    dateOfDeath: (json.dateOfDeath != null) ? LocalDate.parseIso(json.dateOfDeath) : null
  }
}


export function deserializeJsonPersonWithChildrenDTO(json: JsonOf<PersonWithChildrenDTO>): PersonWithChildrenDTO {
  return {
    ...json,
    children: json.children.map(e => deserializeJsonPersonWithChildrenDTO(e)),
    dateOfBirth: LocalDate.parseIso(json.dateOfBirth),
    dateOfDeath: (json.dateOfDeath != null) ? LocalDate.parseIso(json.dateOfDeath) : null,
    restrictedDetails: deserializeJsonRestrictedDetails(json.restrictedDetails)
  }
}


export function deserializeJsonRestrictedDetails(json: JsonOf<RestrictedDetails>): RestrictedDetails {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null
  }
}


export function deserializeJsonScheduledDaycareRole(json: JsonOf<ScheduledDaycareRole>): ScheduledDaycareRole {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null,
    startDate: LocalDate.parseIso(json.startDate)
  }
}


export function deserializeJsonUpsertEmployeeDaycareRolesRequest(json: JsonOf<UpsertEmployeeDaycareRolesRequest>): UpsertEmployeeDaycareRolesRequest {
  return {
    ...json,
    endDate: (json.endDate != null) ? LocalDate.parseIso(json.endDate) : null,
    startDate: LocalDate.parseIso(json.startDate)
  }
}
